import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  ShieldCheck,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";
import { VERIFY } from "@/constants/testIds";
import { useLang } from "@/lib/useLang";
import { PortalHeader, PortalFooter } from "@/components/PortalChrome";
import { authApi } from "@/lib/api";
import { clearPending, getPending, setPending } from "@/lib/pending";

const CODE_LENGTH = 6;
const CODE_REGEX = /^\d+$/;

function useResendCountdown(initialTargetIso) {
  const [secondsLeft, setSecondsLeft] = useState(() =>
    computeSecondsLeft(initialTargetIso),
  );
  const targetRef = useRef(initialTargetIso);

  useEffect(() => {
    targetRef.current = initialTargetIso;
    setSecondsLeft(computeSecondsLeft(initialTargetIso));
    if (!initialTargetIso) return undefined;
    const id = setInterval(() => {
      setSecondsLeft(computeSecondsLeft(targetRef.current));
    }, 500);
    return () => clearInterval(id);
  }, [initialTargetIso]);

  return secondsLeft;
}

function computeSecondsLeft(iso) {
  if (!iso) return 0;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return 0;
  return Math.max(0, Math.ceil((target - Date.now()) / 1000));
}

/* -------------------------------------------------------------------------- */
/*  Code input — 6 boxes with paste + auto-advance + OTP autofill             */
/* -------------------------------------------------------------------------- */
function CodeInput({ value, onChange, hasError, disabled }) {
  const inputsRef = useRef([]);
  const digits = useMemo(() => {
    const arr = value.split("").slice(0, CODE_LENGTH);
    while (arr.length < CODE_LENGTH) arr.push("");
    return arr;
  }, [value]);

  const focusIndex = useCallback((i) => {
    const el = inputsRef.current[i];
    if (el) {
      el.focus();
      el.select?.();
    }
  }, []);

  function handleInput(i, raw) {
    const cleaned = raw.replace(/\D/g, "");
    if (!cleaned) {
      const next = digits.slice();
      next[i] = "";
      onChange(next.join(""));
      return;
    }
    if (cleaned.length > 1) {
      // Paste or autofill of full/partial code.
      const merged = value.slice(0, i) + cleaned;
      onChange(merged.slice(0, CODE_LENGTH));
      const nextFocus = Math.min(CODE_LENGTH - 1, i + cleaned.length);
      setTimeout(() => focusIndex(nextFocus), 0);
      return;
    }
    const next = digits.slice();
    next[i] = cleaned;
    onChange(next.join(""));
    if (i < CODE_LENGTH - 1) focusIndex(i + 1);
  }

  function handleKeyDown(i, e) {
    if (e.key === "Backspace") {
      if (digits[i]) {
        const next = digits.slice();
        next[i] = "";
        onChange(next.join(""));
      } else if (i > 0) {
        focusIndex(i - 1);
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusIndex(i - 1);
    } else if (e.key === "ArrowRight" && i < CODE_LENGTH - 1) {
      e.preventDefault();
      focusIndex(i + 1);
    }
  }

  function handlePaste(i, e) {
    const pasted = (e.clipboardData?.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const merged = value.slice(0, i) + pasted;
    onChange(merged.slice(0, CODE_LENGTH));
    const nextFocus = Math.min(CODE_LENGTH - 1, i + pasted.length);
    setTimeout(() => focusIndex(nextFocus), 0);
  }

  return (
    <div className="flex justify-between gap-2 sm:gap-3">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (inputsRef.current[i] = el)}
          data-testid={VERIFY.codeBox(i)}
          inputMode="numeric"
          pattern="\d*"
          type="text"
          maxLength={CODE_LENGTH /* allow full paste into single input */}
          value={d}
          disabled={disabled}
          onChange={(e) => handleInput(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`Digit ${i + 1}`}
          aria-invalid={hasError}
          className={`h-14 w-full sm:h-16 rounded-xl border bg-white text-center text-2xl sm:text-3xl font-semibold text-[#0B1930] outline-none transition-colors ${
            hasError
              ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100"
              : "border-[#E6E0D1] focus:border-[#C8A46B] focus:ring-2 focus:ring-[#C8A46B]/30"
          } disabled:bg-[#F5F1EA] disabled:text-[#9AA1AC]`}
        />
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */
export default function VerifyCode() {
  const navigate = useNavigate();
  const { lang, t, toggle } = useLang();
  const [pending, setPendingState] = useState(() => getPending());
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | resending | locked
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Redirect to landing if no pending flow.
  useEffect(() => {
    if (!pending?.pending_id) {
      navigate("/", { replace: true });
    }
  }, [pending, navigate]);

  const resendSecondsLeft = useResendCountdown(
    pending?.resend_available_at || null,
  );
  const canResend =
    status !== "resending" && status !== "submitting" && resendSecondsLeft <= 0;

  const onSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (status === "submitting" || status === "locked") return;
      if (code.length !== CODE_LENGTH || !CODE_REGEX.test(code)) {
        setError(t.verify.errorIncomplete);
        return;
      }
      setError("");
      setNotice("");
      setStatus("submitting");
      try {
        const { data } = await authApi.verifyCode(pending.pending_id, code);
        clearPending();
        setNotice(t.verify.successRedirect);
        navigate(data.redirect_to || "/portal/dashboard", { replace: true });
      } catch (err) {
        setStatus("idle");
        setCode("");
        if (err?.status === 423) {
          setStatus("locked");
          setError(t.verify.errorLocked);
        } else if (err?.status === 400) {
          const detail = err.detail || t.verify.errorGeneric;
          if (/expired/i.test(detail)) {
            setError(t.verify.errorExpired);
          } else {
            setError(detail);
          }
        } else if (err?.status === 429) {
          setError(err.detail);
        } else {
          setError(err?.detail || t.verify.errorGeneric);
        }
      }
    },
    [code, navigate, pending, status, t.verify],
  );

  // Auto-submit when the customer completes 6 digits (feels magical +
  // mirrors mobile autofill UX).
  useEffect(() => {
    if (
      code.length === CODE_LENGTH &&
      CODE_REGEX.test(code) &&
      status === "idle"
    ) {
      onSubmit();
    }
  }, [code, status, onSubmit]);

  async function onResend() {
    if (!canResend) return;
    setStatus("resending");
    setError("");
    setNotice("");
    setCode("");
    try {
      const { data } = await authApi.resendCode(pending.pending_id);
      const updated = {
        ...pending,
        pending_id: data.pending_id,
        contact_type: data.contact_type,
        contact_display_masked: data.contact_display_masked,
        resend_available_at: data.resend_available_at,
      };
      setPending(updated);
      setPendingState(updated);
      setNotice(t.verify.resending);
      setStatus("idle");
    } catch (err) {
      setStatus("idle");
      setError(err?.detail || t.verify.errorGeneric);
    }
  }

  function onChangeContact() {
    clearPending();
    navigate("/", { replace: true });
  }

  if (!pending?.pending_id) return null;

  return (
    <div
      className="min-h-screen flex flex-col zmex-page-bg"
      data-testid={VERIFY.page}
    >
      <PortalHeader t={t} lang={lang} onToggleLang={toggle} />

      <main className="relative flex-1">
        <div className="relative z-10 max-w-xl mx-auto px-4 sm:px-6 lg:px-10 pt-14 sm:pt-20 pb-16 sm:pb-20">
          <div className="rounded-3xl bg-white border border-[#EDE5D4] shadow-[0_12px_40px_rgba(11,25,48,0.06)] p-6 sm:p-10">
            <div className="flex flex-col items-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F0E9DC]">
                <ShieldCheck
                  className="h-8 w-8 text-[#0B1930]"
                  strokeWidth={1.75}
                />
              </span>
              <h1
                className="mt-5 font-heading uppercase tracking-[0.02em] text-[#0B1930] text-2xl sm:text-3xl text-center"
                data-testid={VERIFY.heading}
              >
                {t.verify.heading}
              </h1>
              <p className="mt-3 text-center text-[15px] text-[#4B5563]">
                {t.verify.sentTo}{" "}
                <span
                  className="font-semibold text-[#0B1930]"
                  data-testid={VERIFY.destination}
                >
                  {pending.contact_display_masked}
                </span>
              </p>
              {pending.contact_type === "email" && (
                <p className="mt-2 text-center text-xs text-[#9AA1AC]">
                  {t.verify.checkSpamHint}
                </p>
              )}
            </div>

            <form onSubmit={onSubmit} className="mt-8" noValidate>
              <label className="sr-only" htmlFor={VERIFY.codeBox(0)}>
                {t.verify.codeLabel}
              </label>
              <CodeInput
                value={code}
                onChange={setCode}
                hasError={!!error}
                disabled={status === "submitting" || status === "locked"}
              />

              {error && (
                <p
                  className="mt-4 flex items-start gap-1.5 text-sm font-medium text-red-600"
                  role="alert"
                  aria-live="polite"
                  data-testid={
                    status === "locked" ? VERIFY.lockedState : VERIFY.errorMsg
                  }
                >
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  {error}
                </p>
              )}
              {notice && !error && (
                <p
                  className="mt-4 text-sm text-[#4B5563]"
                  aria-live="polite"
                >
                  {notice}
                </p>
              )}

              <button
                type="submit"
                disabled={
                  status === "submitting" ||
                  status === "locked" ||
                  code.length !== CODE_LENGTH
                }
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm sm:text-[15px] font-bold tracking-wide uppercase bg-[#0B1930] hover:bg-[#162B4D] text-white transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid={VERIFY.submitBtn}
              >
                {status === "submitting" ? (
                  <>
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      data-testid={VERIFY.loadingIndicator}
                    />
                    {t.verify.submitting}
                  </>
                ) : (
                  t.verify.submit
                )}
              </button>
            </form>

            <div className="mt-8 flex flex-col items-center gap-3 border-t border-[#EDE5D4] pt-6">
              <p className="text-sm text-[#4B5563] text-center">
                {t.verify.resendPrompt}{" "}
                <button
                  type="button"
                  onClick={onResend}
                  disabled={!canResend}
                  className="font-semibold text-[#B8925A] hover:text-[#0B1930] transition-colors underline-offset-4 hover:underline disabled:no-underline disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid={VERIFY.resendBtn}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <RefreshCw
                      className={`h-3.5 w-3.5 ${
                        status === "resending" ? "animate-spin" : ""
                      }`}
                    />
                    {t.verify.resendCta}
                  </span>
                </button>
              </p>
              {resendSecondsLeft > 0 && (
                <p
                  className="text-xs text-[#9AA1AC]"
                  data-testid={VERIFY.resendCountdown}
                  aria-live="polite"
                >
                  {t.verify.resendIn.replace(
                    "{seconds}",
                    String(resendSecondsLeft),
                  )}
                </p>
              )}
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 pt-2">
                <button
                  type="button"
                  onClick={onChangeContact}
                  className="text-xs font-medium text-[#4B5563] hover:text-[#0B1930] transition-colors"
                  data-testid={VERIFY.changeContactBtn}
                >
                  {t.verify.changeContact}
                </button>
                <button
                  type="button"
                  onClick={onChangeContact}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#4B5563] hover:text-[#0B1930] transition-colors"
                  data-testid={VERIFY.returnLoginBtn}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t.verify.returnLogin}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <PortalFooter t={t} />
    </div>
  );
}
